import * as React from "react";
import { useHistory } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Spinner } from "@fluentui/react/lib/Spinner";

import { SubHeader, AgreementDetails, EmailThreadUpload, EmailAttachmentUpload } from "./components";
import Backdrop from "../../../../components/backdrop/Backdrop";
import { UPLOAD_FLOW, UPLOAD_REQUEST_FROM, UPLOAD_TYPE } from "../../../../shared/constants/uploadParams";
import { TEXT } from "../../../../shared/constants/texts";
import { VIEW_FOLDER_AND_DOCUMENTS } from "../../../../shared/constants/addToICIToggleView";
import { ROUTE } from "../../../../shared/constants/routes";
import { logError } from "../../../../shared/services/errorLogging";
import { callToUploadApi } from "../../utils";
import {
  INITIAL_FORM_DATA,
  resetIndexSingleEmailUpload,
  setIsMajorVersionConfigured,
  setIsVersionLabelsConfigured,
  setMajorVersionStringLocalized,
  setVersionLabels,
  showSingleEmailUpload,
  stopUploadingSingleEmailThread,
  storeEmailAttachmentFormData,
  storeEmailThreadFormData,
  storeUploadSingleEmailThreadData,
  startFetchingAssociationAndFolderForAdvancePage,
  stopFetchingAssociationAndFolderForAdvancePage,
} from "../../redux/slice";
import {
  associationPartOneApiCall,
  associationPartTwoApiCall,
  folderApiCall,
  getLocalizedMajorVersionStringApiCall,
  getMajorMinorVersionFlagApiCall,
  getVersionLabelFlagApiCall,
  getVersionLabelListApiCall,
} from "../../redux/apiCalls";
import {
  transformFolderData,
  updateFolderData,
  transformAssociationPartOneData,
  transformAssociationPartTwoData,
  updateAssociationData,
} from "../../utilFunctions";

const SingleEmailUpload: React.FC = (props) => {
  // @ts-ignore
  const { location } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const state = useSelector((state: any) => state.addToIciSlice);
  const {
    isUploadingSingleEmailThreadUpload,
    singleEmailData,
    emailAttachmentFormData,
    emailThreadFormData,
    selectedViewFolderAndDocuments,
    areAttachmentsIncluded,
    isUploadingSingleEmailUpload,
    isUploadDoneSingleEmailUpload,
    selectedAssociation,
    defaultAssociationOfAgreement,
    isFetchingAssociationAndFolder,
    selectedAgreement,
  } = state;
  const [isEmailThreadUploadable, setIsEmailThreadUploadable] = React.useState(false);
  const [isEmailAttachmentUploadable, setIsEmailAttachmentUploadable] = React.useState(false);
  const errorValidationMsg =
    useSelector((state: any) => state.errorValidation.validationError) ||
    JSON.parse(localStorage.getItem(TEXT.LOCAL_STORAGE_VALIDATION_MSG_KEY));
  const apiCallParams = {
    dispatch: dispatch,
    history: history,
    errorValidationMsg: errorValidationMsg,
  };

  React.useEffect(() => {
    if (location && location.state) {
      const params = location.state;
      if (params && params.from) {
        const from = params.from;
        if (from === UPLOAD_REQUEST_FROM.AGREEMENT) {
          if (params.defaultAssociation) {
            callToUploadApi({
              ...apiCallParams,
              state: state,
              dnd: false,
              type: UPLOAD_TYPE.NEW_DOCUMENT,
              from: UPLOAD_REQUEST_FROM.AGREEMENT,
              flow: UPLOAD_FLOW.DRAG_AND_DROP_SINGLE_EMAIL_THREAD,
              uploadData: { defaultAssociation: params.defaultAssociation },
              files: [],
            });
          } else {
            dispatch(storeUploadSingleEmailThreadData(null));
            dispatch(stopUploadingSingleEmailThread());
          }
        }
        if (from === UPLOAD_REQUEST_FROM.ASSOCIATION) {
          callToUploadApi({
            ...apiCallParams,
            state: state,
            dnd: false,
            type: UPLOAD_TYPE.NEW_DOCUMENT,
            from: UPLOAD_REQUEST_FROM.ASSOCIATION,
            flow: UPLOAD_FLOW.DRAG_AND_DROP_SINGLE_EMAIL_THREAD,
            uploadData: {},
            files: [],
          });
        }
        if (from === UPLOAD_REQUEST_FROM.FOLDER) {
          callToUploadApi({
            ...apiCallParams,
            state: state,
            dnd: false,
            type: UPLOAD_TYPE.NEW_DOCUMENT,
            from: UPLOAD_REQUEST_FROM.FOLDER,
            flow: UPLOAD_FLOW.DRAG_AND_DROP_SINGLE_EMAIL_THREAD,
            uploadData: {},
            files: [],
          });
        }
      } else {
        dispatch(storeUploadSingleEmailThreadData(null));
        dispatch(stopUploadingSingleEmailThread());
        dispatch(startFetchingAssociationAndFolderForAdvancePage());
        getAssociationsAndFolders();
        dispatch(stopFetchingAssociationAndFolderForAdvancePage());
      }
    } else {
      dispatch(storeUploadSingleEmailThreadData(null));
      dispatch(stopUploadingSingleEmailThread());
    }
    if (selectedAssociation || defaultAssociationOfAgreement) {
      const updatedEmailThreadFormData = {
        ...INITIAL_FORM_DATA,
        selectedAssociation: selectedAssociation
          ? selectedAssociation
          : defaultAssociationOfAgreement
            ? defaultAssociationOfAgreement
            : null,
      };
      dispatch(storeEmailThreadFormData(updatedEmailThreadFormData));
      const attachments = singleEmailData.attachments || [];
      const attachmentsData =
        attachments && attachments.length > 0
          ? attachments.map((attachment) => {
              return {
                attachment,
                ...INITIAL_FORM_DATA,
                selectedAssociation: selectedAssociation
                  ? selectedAssociation
                  : defaultAssociationOfAgreement
                    ? defaultAssociationOfAgreement
                    : null,
              };
            })
          : [];
      dispatch(storeEmailAttachmentFormData(attachmentsData));
    } else {
      const updatedEmailThreadFormData = {
        ...INITIAL_FORM_DATA,
      };
      dispatch(storeEmailThreadFormData(updatedEmailThreadFormData));
      const attachments = singleEmailData.attachments || [];
      const attachmentsData =
        attachments && attachments.length > 0
          ? attachments.map((attachment) => {
              return {
                attachment,
                ...INITIAL_FORM_DATA,
              };
            })
          : [];
      dispatch(storeEmailAttachmentFormData(attachmentsData));
    }
    callAdvancedVersioningApis();
    return () => {
      dispatch(resetIndexSingleEmailUpload());
    };
  }, []);

  const getAssociationsAndFolders = async () => {
    await getAssociations();
    await getFolders();
  };

  const getAssociations = async () => {
    const { sysId, entityName } = selectedAgreement;
    const requestDataPartOne = { sysId: sysId, entityName: entityName };
    const associationPartOneResponse = await associationPartOneApiCall({
      ...apiCallParams,
      requestData: requestDataPartOne,
      retryFunctionCall: () => getAssociations(),
    });
    const { response: responsePartOne, error: errorPartOne } = associationPartOneResponse;
    if (responsePartOne) {
      const associationIds = transformAssociationPartOneData(responsePartOne);
      const requestDataPartTwo = { ids: associationIds };
      const associationPartTwoResponse = await associationPartTwoApiCall({
        ...apiCallParams,
        requestData: requestDataPartTwo,
        retryFunctionCall: () => getAssociations(),
      });
      const { response: responsePartTwo, error: errorPartTwo } = associationPartTwoResponse;
      if (responsePartTwo) {
        const associations = transformAssociationPartTwoData({ responsePartOne, responsePartTwo });
        updateAssociationData(associations);
      }
      if (errorPartTwo) {
        updateAssociationData([]);
        logError({
          dispatch: dispatch,
          err: errorPartTwo,
          callerInfo: {
            callerFunction:
              "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getAssociations",
            callerApi: "api/Instance/FindProjections",
          },
        });
      }
    }
    if (errorPartOne) {
      updateAssociationData([]);
      logError({
        dispatch: dispatch,
        err: errorPartOne,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getAssociations",
          callerApi:
            "Agreement/GetAssociationsWithEvaluatedDynamicAssociation?agreementId=&contractType=&evaluateDynamicAssociations=true&skipAmendment=true&skipPeer=true&skipMaster=true",
        },
      });
    }
  };

  const getFolders = async () => {
    const { entityName } = selectedAgreement;
    const requestData = { entityName: entityName };
    const folderResponse = await folderApiCall({
      ...apiCallParams,
      requestData: requestData,
      retryFunctionCall: () => getFolders(),
    });
    const { response, error } = folderResponse;
    if (response) {
      const folderData = transformFolderData(response);
      console.log(folderData);
      const { transformedFolderData, finalFolderData } = folderData;
      if (!transformedFolderData || !finalFolderData) {
        updateFolderData(TEXT.NO_FOLDER_STRUCTURE_CONFIGURED);
      }
      if (finalFolderData) {
        updateFolderData(finalFolderData);
      }
    }
    if (error) {
      updateFolderData(null);
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|drag-and-drop|agreement-listing|AgreementListing - getFolders",
          callerApi: "api/instance/FindProjectionsKeyValuePair",
        },
      });
    }
  };

  const callAdvancedVersioningApis = () => {
    isMajorVersionConfigured();
    isVersionLabelConfigured();
    getVersionLabelsList();
    getMajorVersionLocalizedString();
  };

  const isMajorVersionConfigured = async () => {
    const majorVersionConfiguredResponse = await getMajorMinorVersionFlagApiCall({
      ...apiCallParams,
    });
    const { response, error } = majorVersionConfiguredResponse;
    if (response) {
      const isMajorVersionConfigured =
        JSON.parse(response[TEXT.MAJOR_MINOR_VERSION_KEY]).UserDefinedVersioningEnabled || false;
      dispatch(setIsMajorVersionConfigured(JSON.parse(isMajorVersionConfigured)));
    }
    if (error) {
      dispatch(setIsMajorVersionConfigured(false));
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|index - isMajorVersionConfigured",
          callerApi: "api/Configurations?configKeys=",
        },
      });
    }
  };

  const isVersionLabelConfigured = async () => {
    const versionLabelConfiguredResponse = await getVersionLabelFlagApiCall({
      ...apiCallParams,
    });
    const { response, error } = versionLabelConfiguredResponse;
    if (response) {
      const isVersionLabelConfigured =
        JSON.parse(response[TEXT.VERSION_LABEL_KEY]).EnableDocumentVersionLabels || false;
      dispatch(setIsVersionLabelsConfigured(JSON.parse(isVersionLabelConfigured)));
    }
    if (error) {
      dispatch(setIsVersionLabelsConfigured(false));
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|index - isVersionLabelConfigured",
          callerApi: "api/Configurations?configKeys=",
        },
      });
    }
  };

  const getVersionLabelsList = async () => {
    const versionLabelsListResponse = await getVersionLabelListApiCall({
      ...apiCallParams,
    });
    const { response, error } = versionLabelsListResponse;
    if (response) {
      let versionLabels = response;
      if (versionLabels && versionLabels.length > 0) {
        versionLabels = versionLabels.map((versionLabel) => {
          return {
            ...versionLabel,
            label: versionLabel.DisplayName,
            value: versionLabel.DisplayName,
          };
        });
      }
      dispatch(setVersionLabels(versionLabels));
    }
    if (error) {
      dispatch(setVersionLabels([]));
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|index - getVersionLabelsList",
          callerApi: "api/VersionLabelMaster/GetVersionLabels",
        },
      });
    }
  };

  const getMajorVersionLocalizedString = async () => {
    const majorVersionLocalizedStringResponse = await getLocalizedMajorVersionStringApiCall({
      ...apiCallParams,
    });
    const { response, error } = majorVersionLocalizedStringResponse;
    if (response) {
      dispatch(setMajorVersionStringLocalized(response.WordAI_Label_DocumentVersion_UserDefined_Upload));
    }
    if (error) {
      dispatch(setMajorVersionStringLocalized(TEXT.DEFAULT_MAJOR_VERSION_TEXT));
      logError({
        dispatch: dispatch,
        err: error,
        callerInfo: {
          callerFunction:
            "ClientApp|src|taskpane|containers|add-to-ici|flows|single-email-upload|index - getMajorVersionLocalizedString",
          callerApi: "api/StringResources",
        },
      });
    }
  };

  React.useEffect(() => {
    if (singleEmailData) {
      const attachments = singleEmailData.attachments || [];
      const attachmentsData =
        attachments && attachments.length > 0
          ? attachments.map((attachment) => {
              return {
                attachment,
                ...INITIAL_FORM_DATA,
                selectedAssociation: selectedAssociation
                  ? selectedAssociation
                  : defaultAssociationOfAgreement
                    ? defaultAssociationOfAgreement
                    : null,
              };
            })
          : [];
      dispatch(storeEmailAttachmentFormData(attachmentsData));
      dispatch(
        storeEmailThreadFormData({
          ...INITIAL_FORM_DATA,
          selectedAssociation: selectedAssociation
            ? selectedAssociation
            : defaultAssociationOfAgreement
              ? defaultAssociationOfAgreement
              : null,
        })
      );
      dispatch(storeUploadSingleEmailThreadData(null));
    } else {
      dispatch(storeEmailThreadFormData(INITIAL_FORM_DATA));
      dispatch(storeEmailAttachmentFormData([]));
      dispatch(storeUploadSingleEmailThreadData(null));
    }
  }, [singleEmailData]);

  React.useEffect(() => {
    const data = { ...emailThreadFormData };
    const {
      selectedUploadType,
      selectedAssociation,
      newDocumentName,
      selectedFolderNewDocument,
      replaceAssociationInstance,
      replaceFolderInstance,
    } = data;
    const isFolderView =
      selectedViewFolderAndDocuments && selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id;
    if (
      (selectedAssociation &&
        selectedUploadType &&
        selectedUploadType !== TEXT.SKIP &&
        (replaceAssociationInstance || (newDocumentName && !isFolderView))) ||
      (selectedAssociation &&
        selectedUploadType &&
        selectedUploadType !== TEXT.SKIP &&
        (replaceFolderInstance || (newDocumentName && selectedFolderNewDocument))) ||
      (selectedAssociation && selectedUploadType && selectedUploadType === TEXT.KEEP_BOTH && newDocumentName)
    ) {
      setIsEmailThreadUploadable(true);
    } else {
      setIsEmailThreadUploadable(false);
    }
  }, [emailThreadFormData]);

  React.useEffect(() => {
    const data = [...emailAttachmentFormData];
    const isFolderView =
      selectedViewFolderAndDocuments && selectedViewFolderAndDocuments.id === VIEW_FOLDER_AND_DOCUMENTS[1].id;
    const isAnyAttachmentUploadable = data.some((item) => {
      const {
        selectedAssociation,
        selectedUploadType,
        replaceAssociationInstance,
        newDocumentName,
        replaceFolderInstance,
        selectedFolderNewDocument,
      } = item;
      return (
        (selectedAssociation &&
          selectedUploadType &&
          (replaceAssociationInstance || (newDocumentName && !isFolderView))) ||
        (selectedAssociation && selectedAssociation.id === TEXT.AGREEMENT_NEW_VERSION) ||
        (selectedAssociation &&
          selectedUploadType &&
          (replaceFolderInstance || (newDocumentName && selectedFolderNewDocument)))
      );
    });
    setIsEmailAttachmentUploadable(isAnyAttachmentUploadable);
  }, [emailAttachmentFormData]);

  React.useEffect(() => {
    if (isUploadDoneSingleEmailUpload) {
      history.push(ROUTE.ADD_TO_ICI);
    }
  }, [isUploadDoneSingleEmailUpload]);

  const onAddToIciClicked = () => {
    dispatch(showSingleEmailUpload());
    callToUploadApi({
      ...apiCallParams,
      state: state,
      dnd: false,
      type:
        emailThreadFormData &&
        emailThreadFormData.selectedUploadType &&
        emailThreadFormData.selectedUploadType === TEXT.KEEP_BOTH
          ? UPLOAD_TYPE.KEEP_BOTH
          : UPLOAD_TYPE.NEW_DOCUMENT,
      from: "",
      flow: UPLOAD_FLOW.SINGLE_EMAIL_UPLOAD,
      uploadData: {
        emailThreadFormData: isEmailThreadUploadable ? emailThreadFormData : null,
        emailAttachmentFormData: isEmailAttachmentUploadable ? emailAttachmentFormData : null,
      },
    });
  };

  console.log("state in add to ici (single email upload):", state);

  return (
    <div className="single-email-upload-add-to-ici">
      <SubHeader />
      <AgreementDetails />
      {!isUploadingSingleEmailThreadUpload || !isFetchingAssociationAndFolder ? (
        <React.Fragment>
          <div className="content-single-email-upload-add-to-ici">
            <EmailThreadUpload />
            {emailAttachmentFormData && emailAttachmentFormData.length > 0 && areAttachmentsIncluded ? (
              <EmailAttachmentUpload />
            ) : null}
          </div>
          <div
            className={
              isUploadingSingleEmailUpload
                ? "footer-single-email-upload-add-to-ici footer-single-email-upload-add-to-ici-z-index"
                : "footer-single-email-upload-add-to-ici"
            }
          >
            {!isUploadingSingleEmailUpload ? (
              <button disabled={!(isEmailThreadUploadable || isEmailAttachmentUploadable)} onClick={onAddToIciClicked}>
                {TEXT.ADD_TO_ICI}
              </button>
            ) : (
              <Spinner label={TEXT.UPLOADING_TO_ICI} labelPosition="right" />
            )}
          </div>
        </React.Fragment>
      ) : (
        <div className="getting-details-single-email-thread-upload">
          <Spinner label={TEXT.GETTING_DETAILS} labelPosition="bottom" />
        </div>
      )}
      <Backdrop show={isUploadingSingleEmailUpload} />
    </div>
  );
};

export default SingleEmailUpload;
